import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  getEditorInstance,
  getPlugins,
} from "../DocumentPreview/TratoFormat/TratoFormat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getParticipantsMap } from "../../context/editor";
import { useConstant } from "../../hooks/useConstant";
import { Slate, useSlate, withReact, ReactEditor } from "slate-react";
import { insertVariable } from "../DocumentPreview/TratoFormat/Elements/VariableElement/transforms/insert-variable.js";
import clsx from "clsx";

import {
  EditablePlugins,
  HeadingToolbar,
  MARK_BOLD,
  MARK_ITALIC,
  ToolbarMark,
  DEFAULTS_BOLD,
  DEFAULTS_CODE,
  DEFAULTS_HIGHLIGHT,
  DEFAULTS_ITALIC,
  DEFAULTS_PARAGRAPH,
  DEFAULTS_STRIKETHROUGH,
  DEFAULTS_SUBSUPSCRIPT,
  DEFAULTS_UNDERLINE,
  DEFAULTS_KBD,
  ELEMENT_ALIGN_LEFT,
  ELEMENT_ALIGN_CENTER,
  ELEMENT_ALIGN_RIGHT,
  ELEMENT_ALIGN_JUSTIFY,
  DEFAULTS_ALIGN,
  DEFAULTS_BLOCKQUOTE,
  DEFAULTS_CODE_BLOCK,
  DEFAULTS_HEADING,
  DEFAULTS_IMAGE,
  DEFAULTS_LINK,
  DEFAULTS_LIST,
  DEFAULTS_MEDIA_EMBED,
  DEFAULTS_MENTION,
  DEFAULTS_SEARCH_HIGHLIGHT,
  DEFAULTS_TABLE,
  DEFAULTS_TODO_LIST,
  getPreventDefaultHandler,
} from "@udecode/slate-plugins";

import { useTranslation } from "react-i18next";
import {
  upsertAlign,
  isAlignActive,
} from "../../components/DocumentPreview/TratoFormat";

import {
  faBold,
  faItalic,
  faAlignCenter,
  faAlignJustify,
  faAlignLeft,
  faAlignRight,
} from "@fortawesome/free-solid-svg-icons";

export const options = {
  // marks
  ...DEFAULTS_BOLD,
  ...DEFAULTS_ITALIC,
  ...DEFAULTS_UNDERLINE,
  ...DEFAULTS_STRIKETHROUGH,
  ...DEFAULTS_CODE,
  ...DEFAULTS_KBD,
  ...DEFAULTS_SUBSUPSCRIPT,
  ...DEFAULTS_HIGHLIGHT,
  ...DEFAULTS_PARAGRAPH,
  ...DEFAULTS_MENTION,
  ...DEFAULTS_BLOCKQUOTE,
  ...DEFAULTS_CODE_BLOCK,
  ...DEFAULTS_LINK,
  ...DEFAULTS_IMAGE,
  ...DEFAULTS_MEDIA_EMBED,
  ...DEFAULTS_TODO_LIST,
  ...DEFAULTS_TABLE,
  ...DEFAULTS_LIST,
  ...DEFAULTS_HEADING,
  ...DEFAULTS_ALIGN,
  // marks
  ...DEFAULTS_BOLD,
  ...DEFAULTS_ITALIC,
  ...DEFAULTS_UNDERLINE,
  ...DEFAULTS_STRIKETHROUGH,
  ...DEFAULTS_CODE,
  ...DEFAULTS_KBD,
  ...DEFAULTS_SUBSUPSCRIPT,
  ...DEFAULTS_HIGHLIGHT,
  ...DEFAULTS_SEARCH_HIGHLIGHT,
  ...DEFAULTS_ALIGN,
};

const editorStyles = {
  backgroundColor: "white",
  maxWidth: "1800",
  // height: 100,
  // overflowY: "auto",
  font: "normal normal normal 12px Muli",
};

const readOnly = {
  color: "#6F767D",
};

const edit = {
  color: "#000000",
};

export default function Editor({
  isReadOnly,
  children,
  onChangeClause,
  onCancelEdit,
  contract,
  textAlign,
}) {
  const [value, setValue] = useState(children);
  const { t } = useTranslation("common");

  // const editorRef = useRef();
  // if (!editorRef.current) editorRef.current = withReact(getEditorInstance());
  // const editor = editorRef.current;
  //useMemo
  const editor = useMemo(() => getEditorInstance(), []);
  //useState
  // const [editor] = useState(withReact(useConstant(() => getEditorInstance())));
  // const editor = useConstant(() => getEditorInstance());

  const handleChange = (nodes) => {
    setValue(nodes);
  };

  const plugins = useMemo(() => {
    let participants = getParticipantsMap(contract?.participants);
    return getPlugins({
      contract: contract,
      participants,
    });
  }, [contract]);

  const ToolbarItem = React.forwardRef(({ active, icon, onClick }, ref) => {
    return (
      <span
        innerRef={ref}
        data-testid="ToolbarButton"
        className={clsx(
          active && "slate-ToolbarButton-active",
          "slate-ToolbarButton root-35 text-center"
        )}
        onMouseDown={onClick}
      >
        {icon}
      </span>
    );
  });

  const ToolbarAlign = ({ icon, type = "left" }) => {
    const editor = useSlate();
    const unwrapTypes = [
      ELEMENT_ALIGN_LEFT,
      ELEMENT_ALIGN_CENTER,
      ELEMENT_ALIGN_RIGHT,
      ELEMENT_ALIGN_JUSTIFY,
    ];

    return (
      <ToolbarItem
        icon={icon}
        active={isAlignActive(editor, { type })}
        onClick={getPreventDefaultHandler(upsertAlign, editor, {
          type,
          unwrapTypes,
        })}
      />
    );
  };

  return (
    <>
      <div
        className={clsx("px-1", !isReadOnly && "border border-dark rounded")}
        style={isReadOnly ? readOnly : edit}
      >
        {value && editor && (
          <Slate editor={editor} value={value} onChange={handleChange}>
            {!isReadOnly && (
              <HeadingToolbar className={"p-5 text-center"}>
                <ToolbarMark
                  type={MARK_BOLD}
                  icon={<FontAwesomeIcon size="xs" icon={faBold} />}
                />
                <ToolbarMark
                  type={MARK_ITALIC}
                  icon={<FontAwesomeIcon size="xs" icon={faItalic} />}
                />
                <ToolbarAlign
                  type="left"
                  icon={<FontAwesomeIcon size="xs" icon={faAlignLeft} />}
                />
                <ToolbarAlign
                  type="center"
                  icon={<FontAwesomeIcon size="xs" icon={faAlignCenter} />}
                />
                <ToolbarAlign
                  type="right"
                  icon={<FontAwesomeIcon size="xs" icon={faAlignRight} />}
                />
                <ToolbarAlign
                  type="justify"
                  icon={<FontAwesomeIcon size="xs" icon={faAlignJustify} />}
                />
              </HeadingToolbar>
            )}
            <EditablePlugins
              readOnly={isReadOnly}
              style={{ ...editorStyles, textAlign }}
              plugins={plugins}
              placeholder={t("actions.editorPlaceholder")}
            />
          </Slate>
        )}
      </div>
      {!isReadOnly && (
        <div className="text-right pt-4">
          {/* <button
            className="mr-2 mt-2 text-light btn btn-secondary"
            onClick={onCancelEdit}
          >
            Cancelar
          </button> */}
          <button
            className=" mt-2 btn btn-accent-100  text-light"
            onClick={onChangeClause({ value })}
          >
            {t("saveButton")}
          </button>
        </div>
      )}
    </>
  );
}
